/**
 * AudioEngine facade (plan P2, race audio 2): nothing is built until enable() runs from a user gesture, which
 * creates and resumes the AudioContext there (and unlocks the radio element). The shell calls update() once per
 * frame. Buses: engine worklet -> gate (100 ms fade on off states) -> engine duck -> destination; ambience
 * worklet (music + crowd) -> gate (300 ms) -> ambience duck -> destination; commentary -> commentary duck ->
 * destination. The radio element is outside Web Audio, so radio audio ducks the other buses.
 * Mix, loudest first: commentary 1.0 > radio audio (element, 1.0) > music 0.5 > crowd 0.3 > engines (6 voices x 0.55 on a 0.45 bus).
 * Commentary ducks engines to 0.4, music and crowd to 0.35; radio audio ducks them to 0.15 / 0.2. Ducks attack fast
 * (80 ms time constant) and release slowly (700 ms for music and crowd, 250 ms for engines).
 *
 * H4: while the player is paused (or the tab hidden) the context suspends once the gates have
 *  faded, and play resumes it — the worklets then cost nothing instead of rendering silence. P-3
 *  (iOS, unverified): `navigator.audioSession.type = 'playback'` where it exists, and a resume of
 *  an 'interrupted' context inside the gesture.
 */
import { CommentaryDriver, type CommentaryCtxLike } from './commentary-driver.ts';
import { crowdZoomGain } from './ambience.ts';
import { createAmbienceNode, createEngineNode, postAmbience, postParams, postSeed, type EngineNodeLike } from './engine.ts';
import { ModelledEngine } from './modelled-engine.ts';
import { MAX_VOICES, type VoiceParams } from './engine-dsp.ts';
import { CROSSFADE_S, VoiceSelector, distanceGain, panOf, ratePolicy } from './policy.ts';
import { seedOf } from './music-dsp.ts';
import { RadioElementDriver, type AudioElementLike } from './radio-driver.ts';
import type { AudioInput, AudioState, CommentaryFile, RadioFile } from './types.ts';

export interface ParamLike {
  value: number;
  setValueAtTime(v: number, t: number): unknown;
  linearRampToValueAtTime(v: number, t: number): unknown;
  setTargetAtTime(v: number, t: number, tc: number): unknown;
  cancelScheduledValues(t: number): unknown;
}
export interface GainLike { gain: ParamLike; connect(d: unknown): unknown }
export interface CtxLike extends CommentaryCtxLike {
  readonly destination: unknown;
  /** AudioContextState, plus Safari's non-standard "interrupted" (P-3) */
  readonly state: AudioContextState | 'interrupted';
  suspend(): Promise<void>;
  resume(): Promise<void>;
  close(): Promise<void>;
  createGain(): GainLike;
}
export interface EngineDeps {
  createContext(): CtxLike;
  createEngineNode(ctx: CtxLike): Promise<EngineNodeLike>;
  createAmbienceNode(ctx: CtxLike): Promise<EngineNodeLike>;
  createElement(): AudioElementLike;
  fetchBytes?: (url: string) => Promise<ArrayBuffer>;
}

const defaultDeps: EngineDeps = {
  // r141: touch devices get the larger 'playback' buffer — a phone core under render load underran the small one (choppy audio)
  createContext: () => new AudioContext({ latencyHint: matchMedia('(pointer: coarse)').matches ? 'playback' : 'interactive' }) as unknown as CtxLike,
  createEngineNode: (ctx) => createEngineNode(ctx as unknown as BaseAudioContext),
  createAmbienceNode: (ctx) => createAmbienceNode(ctx as unknown as BaseAudioContext),
  createElement: () => new Audio(),
};

/** P-3 (iOS, unverified on device, P13): Safari's `navigator.audioSession`, feature-checked.
 *  `type = 'playback'` keeps race audio playing with the ringer switch silent. */
interface AudioSessionLike { type: string }
function setAudioSessionPlayback(): void {
  const nav = navigator as Navigator & { audioSession?: AudioSessionLike };
  if (nav.audioSession) nav.audioSession.type = 'playback';
}

const FADE_S = 0.1;
const AMB_FADE_S = 0.3;
const VOICE_GAIN = 0.55;
const ENGINE_BUS = 0.45;
const MUSIC_LEVEL = 0.5;
const CROWD_LEVEL = 0.3;
const DUCK_ATTACK_TC = 0.08;
const DUCK_RELEASE_TC = { engine: 0.25, comm: 0.25, amb: 0.7 };

export class AudioEngine {
  private readonly modelled = new ModelledEngine();
  private ctx: CtxLike | null = null;
  private node: EngineNodeLike | null = null;
  private ambNode: EngineNodeLike | null = null;
  private gate: GainLike | null = null;
  private ambGate: GainLike | null = null;
  private ambDuck: GainLike | null = null;
  private engineDuck: GainLike | null = null;
  private commDuck: GainLike | null = null;
  private readonly radio: RadioElementDriver;
  private comm: CommentaryDriver | null = null;
  private commFile: { file: CommentaryFile; base: string; raceOfPres: ((t: number) => number) | undefined } | null = null;
  private readonly selector = new VoiceSelector();
  private lastT = 0;
  private gateTarget = -1;
  private ambGateTarget = -1;
  private duck = { e: 1, c: 1, a: 1 };
  private mix = { voices: 0 };
  private seed = 0;
  private ambienceFailed = false;
  private ambienceOn = false;
  private enabledFlag = false;
  private engineFailed = false;
  private engines: AudioState['engines'] = 'off';
  private radioClip: string | null = null;
  /** H4: the context is held (suspend scheduled) while paused; playing releases it */
  private held = false;
  private holdTimer: ReturnType<typeof setTimeout> | null = null;

  private constructor(private readonly deps: EngineDeps) {
    this.radio = new RadioElementDriver(deps.createElement());
  }

  static create(deps: EngineDeps = defaultDeps): AudioEngine { return new AudioEngine(deps); }

  async enable(): Promise<void> {
    if (this.ctx) return;
    setAudioSessionPlayback(); // P-3: before the context exists, still inside the user gesture
    const ctx = this.deps.createContext();
    this.ctx = ctx;
    void ctx.resume().catch(() => undefined);  // inside the gesture, before any await (an interrupted context too, P-3)
    this.radio.unlock();
    this.enabledFlag = true;
    this.lastT = ctx.currentTime;
    this.gate = ctx.createGain(); this.gate.gain.value = 0;
    this.engineDuck = ctx.createGain(); this.commDuck = ctx.createGain();
    this.engineDuck.gain.value = ENGINE_BUS;
    this.ambGate = ctx.createGain(); this.ambGate.gain.value = 0; this.ambDuck = ctx.createGain();
    this.gate.connect(this.engineDuck); this.engineDuck.connect(ctx.destination); this.commDuck.connect(ctx.destination);
    this.ambGate.connect(this.ambDuck); this.ambDuck.connect(ctx.destination);
    this.gateTarget = 0; this.ambGateTarget = 0;
    this.comm = new CommentaryDriver(ctx, this.commDuck, this.deps.fetchBytes);
    if (this.commFile) this.comm.setFile(this.commFile.file, this.commFile.base, this.commFile.raceOfPres);
    const [eng, amb] = await Promise.allSettled([this.deps.createEngineNode(ctx), this.deps.createAmbienceNode(ctx)]);
    if (this.ctx !== ctx) return;      // disabled while loading
    if (eng.status === 'fulfilled') { eng.value.connect(this.gate); this.node = eng.value; } else this.engineFailed = true;
    if (amb.status === 'fulfilled') { amb.value.connect(this.ambGate); this.ambNode = amb.value; postSeed(amb.value, this.seed); } else this.ambienceFailed = true;
  }

  disable(): void {
    const ctx = this.ctx;
    if (!ctx) return;
    this.releaseHold();
    this.setGate(0);
    this.radio.update(0, false);
    this.comm?.update({ raceT: 0, presT: 0, mode: 'director', playing: false, effectiveRate: 0, buffering: false, dragging: false, hidden: false, listener: { x: 0, y: 0, zoom: 1 }, cars: [], intensity: 0, swell: 0, sting: false }, false);
    this.setAmbGate(0);
    this.enabledFlag = false; this.ctx = null; this.node = null; this.ambNode = null; this.comm = null; this.gate = null; this.ambGate = null; this.ambDuck = null; this.engineDuck = null; this.commDuck = null;
    this.engines = 'off'; this.ambienceOn = false; this.radioClip = null; this.duck = { e: 1, c: 1, a: 1 }; this.mix = { voices: 0 };
    this.gateTarget = -1; this.ambGateTarget = -1;
    setTimeout(() => { void ctx.close(); }, AMB_FADE_S * 1000 + 20);
  }

  /** the race id seeds the music (key, tempo, chords) and the crowd modulation */
  setRace(id: string): void {
    this.seed = seedOf(id);
    if (this.ambNode) postSeed(this.ambNode, this.seed);
  }

  setRadio(file: RadioFile | null): void {
    this.radio.setFile(file);
  }

  setCommentary(file: CommentaryFile | null, baseUrl: string, raceOfPres?: (t: number) => number): void {
    this.commFile = file ? { file, base: baseUrl, raceOfPres } : null;
    this.comm?.setFile(file, baseUrl, raceOfPres);
  }

  private setGate(v: number): void {
    const ctx = this.ctx, g = this.gate;
    if (!ctx || !g || v === this.gateTarget) return;
    this.gateTarget = v;
    const now = ctx.currentTime;
    g.gain.cancelScheduledValues(now);
    g.gain.setValueAtTime(g.gain.value, now);
    g.gain.linearRampToValueAtTime(v, now + FADE_S);
  }

  private setAmbGate(v: number): void {
    const ctx = this.ctx, g = this.ambGate;
    if (!ctx || !g || v === this.ambGateTarget) return;
    this.ambGateTarget = v;
    const now = ctx.currentTime;
    g.gain.cancelScheduledValues(now);
    g.gain.setValueAtTime(g.gain.value, now);
    g.gain.linearRampToValueAtTime(v, now + AMB_FADE_S);
  }

  private setDuck(e: number, c: number, a: number): void {
    const ctx = this.ctx;
    if (!ctx || !this.engineDuck || !this.commDuck || !this.ambDuck) return;
    const go = (k: 'e' | 'c' | 'a', g: GainLike, to: number, scale: number, rel: number): void => {
      if (to === this.duck[k]) return;
      const tc = to < this.duck[k] ? DUCK_ATTACK_TC : rel;
      this.duck[k] = to;
      g.gain.setTargetAtTime(to * scale, ctx.currentTime, tc);
    };
    go('e', this.engineDuck, e, ENGINE_BUS, DUCK_RELEASE_TC.engine);
    go('c', this.commDuck, c, 1, DUCK_RELEASE_TC.comm);
    go('a', this.ambDuck, a, 1, DUCK_RELEASE_TC.amb);
  }

  /** H4: while paused (or the tab hidden — the shell pauses on hide) the whole context suspends
   *  after the gates' fade, so the worklets stop chewing a little core at silence; playing resumes
   *  it. The suspend waits out the 300 ms ambience fade so the fade-out stays audible. */
  private holdContext(): void {
    if (this.held) return;
    this.held = true;
    this.holdTimer = setTimeout(() => {
      this.holdTimer = null;
      if (this.held && this.ctx) void this.ctx.suspend().catch(() => undefined);
    }, AMB_FADE_S * 1000 + 20);
  }

  private releaseHold(): void {
    this.held = false;
    if (this.holdTimer) {
      clearTimeout(this.holdTimer);
      this.holdTimer = null;
    }
  }

  /** P-3: resume a 'suspended' or iOS-'interrupted' context; a no-op while running. */
  private resumeCtx(): void {
    const ctx = this.ctx;
    if (!ctx) return;
    if (ctx.state === 'suspended' || ctx.state === 'interrupted') void ctx.resume().catch(() => undefined);
  }

  update(inp: AudioInput): void {
    const ctx = this.ctx;
    if (!ctx || !this.enabledFlag) return;
    if (inp.playing && !inp.hidden) {
      this.releaseHold();
      this.resumeCtx();      // P-3: also recovers an interrupted context on return to the page
    } else {
      this.holdContext();
    }
    const pol = ratePolicy(inp);
    this.engines = pol.engines;
    const dt = Math.min(Math.max(ctx.currentTime - this.lastT, 0), 0.25);
    this.lastT = ctx.currentTime;

    this.setGate(pol.engines === 'off' ? 0 : 1);
    this.setAmbGate(pol.ambience ? 1 : 0);
    this.ambienceOn = pol.ambience;
    const running = inp.cars.filter((c) => c.running);
    const slots = this.selector.update(dt, running, pol.engines === 'voices');
    const step = dt / CROSSFADE_S;
    const approach = (cur: number, to: number): number => cur + Math.min(Math.max(to - cur, -step), step);
    this.mix.voices = approach(this.mix.voices, pol.engines === 'voices' ? 1 : 0);

    if (this.node) {
      const byIdx = new Map(inp.cars.map((c) => [c.idx, c]));
      const voices: VoiceParams[] = [];
      for (let i = 0; i < MAX_VOICES; i++) {
        const s = slots[i], c = s && s.car >= 0 ? byIdx.get(s.car) : undefined;
        const m = c && inp.modelled ? this.modelled.update(c.idx, inp.raceT, c.speed ?? 0) : c;
        voices.push(s && c && m
          ? { rpm: m.rpm, thr: m.thr / 100, gear: m.gear, gain: s.level * this.mix.voices * VOICE_GAIN * distanceGain(c.dist, inp.listener.zoom), pan: panOf(c.sx) }
          : { rpm: 0, thr: 0, gear: 0, gain: 0, pan: 0 });
      }
      postParams(this.node, inp.raceT, voices);
    }
    if (this.ambNode) {
      postAmbience(this.ambNode, {
        presT: inp.presT, intensity: inp.intensity, music: MUSIC_LEVEL, crowd: CROWD_LEVEL * crowdZoomGain(inp.listener.zoom),
        swell: pol.ambience ? inp.swell : 0, sting: pol.ambience && inp.sting, active: pol.ambience,
      });
    }

    const clip = this.radio.update(inp.raceT, pol.radioAudio);
    this.radioClip = clip?.id ?? null;
    this.comm?.update(inp, pol.commentary);
    const radioOn = this.radio.playing, commOn = this.comm?.playing ?? false;
    this.setDuck(radioOn ? 0.15 : commOn ? 0.4 : 1, radioOn ? 0.25 : 1, radioOn ? 0.2 : commOn ? 0.35 : 1);
  }

  state(): AudioState {
    return {
      enabled: this.enabledFlag,
      engineUnavailable: this.engineFailed,
      ambienceUnavailable: this.ambienceFailed,
      ambience: this.ambienceOn,
      engines: this.engines,
      radio: { clipId: this.radioClip, playing: this.radio.playing, unavailable: [...this.radio.unavailable] },
      commentary: {
        lineId: this.comm?.activeId ?? null, playing: this.comm?.playing ?? false,
        voiceUnavailable: this.comm?.voiceUnavailable ?? false, unavailable: [...(this.comm?.unavailable ?? [])],
      },
      voices: this.selector.slots.map((s) => s.car),
    };
  }
}
